import { useMemo, useState } from "react";

import { formatNum, formatUsd, Hidable, lamportsToUiAmount, percentUiToDecimals } from "@bridgesplit/utils";
import { BsMetaUtil, useTokenListContext } from "@bridgesplit/abf-react";
import {
    Column,
    Row,
    StatColumn,
    Text,
    Tooltip,
    TooltipText,
    ToggleChevron,
    StatProps,
    TextVariant,
    TextColor,
    IconWithBackground,
    OutlinedCard
} from "@bridgesplit/ui";
import { Collapse, Divider } from "@mui/material";
import { RouteOutlined } from "@mui/icons-material";
import { SwapInstructionsResponse } from "@loopscale/pandora-types";
import { ExchangeBadge } from "app/components/swap";

import { PercentAndUsdRow } from "../../common/util";
import { ExecutionDetailsLabels, LoopExecutionDetails } from "./types";
import { useExecutionDetailsLabels } from "./utils";

export function ExecutionDetails({
    executionDetails,
    loading,
    textVariant,
    slippagePresetsUi
}: {
    executionDetails: LoopExecutionDetails | undefined;
    loading: boolean;
    textVariant: TextVariant;
    slippagePresetsUi: number[];
}) {
    const [isExpanded, setIsExpanded] = useState(false);

    const executionDetailsLabels = useExecutionDetailsLabels({
        overrides: executionDetails?.labelOverrides
    });

    const executionErrorImpactColor = getExecutionErrorImpactColor({ executionDetails, slippagePresetsUi });

    const executionDetailsStats = useExecutionDetailsStats({
        executionDetails,
        executionDetailsLabels,
        textVariant
    });

    return (
        <Column spacing={0.5}>
            <ExecutionDetailsCollapableHeader
                executionDetails={executionDetails}
                loading={loading}
                isExpanded={isExpanded}
                setIsExpanded={setIsExpanded}
                executionDetailsTooltip={executionDetailsLabels.executionImpact?.tooltip}
                textVariant={textVariant}
                executionErrorImpactColor={executionErrorImpactColor}
            />
            <Column spacing={isExpanded ? 0.5 : 0}>
                <Collapse in={isExpanded}>
                    <Column sx={{ pt: 1 }} spacing={1}>
                        {!loading && <Divider />}
                        <StatColumn
                            loading={loading}
                            variant={textVariant}
                            spacing={0.5}
                            stats={executionDetailsStats}
                        />
                    </Column>
                </Collapse>
                <ExecutionPriceStat executionDetails={executionDetails} loading={loading} textVariant={textVariant} />
            </Column>
        </Column>
    );
}

function ExecutionPriceStat({
    executionDetails,
    loading,
    textVariant
}: {
    executionDetails: LoopExecutionDetails | undefined;
    loading: boolean;
    textVariant: TextVariant;
}) {
    const { getMetadata } = useTokenListContext();
    const quote = executionDetails?.externalQuote?.quote;

    const sellMeta = getMetadata(quote?.inputMint);
    const buyMeta = getMetadata(quote?.outputMint);

    const sellUi = lamportsToUiAmount(quote?.inputAmount ?? 0, sellMeta?.decimals);
    const expectedOutput = quote?.outputs?.[0]?.expectedAmount;
    const buyUi = lamportsToUiAmount(expectedOutput ?? quote?.minOutputAmount ?? 0, buyMeta?.decimals);

    const rate = sellUi > 0 ? buyUi / sellUi : undefined;
    const sellSymbol = BsMetaUtil.getSymbol(sellMeta, { shortSymbol: true });
    const buySymbol = BsMetaUtil.getSymbol(buyMeta, { shortSymbol: true });

    const rateDisplay =
        rate !== undefined ? `1 ${sellSymbol} ≈ ${formatNum(rate, { customDecimals: 4 })} ${buySymbol}` : undefined;
    const usdPerSell =
        executionDetails?.executionPriceEstimate != null && rate
            ? executionDetails.executionPriceEstimate * rate
            : undefined;
    const usdTooltip =
        usdPerSell != null ? `1 ${sellSymbol} ≈ ${formatUsd(usdPerSell, { maximumFractionDigits: 4 })}` : undefined;

    if (executionDetails?.executionPriceEstimate == null) return null;

    return (
        <StatColumn
            loading={loading}
            stats={[
                {
                    caption: "Execution price",
                    value: (
                        <Row spacing={0.5}>
                            <Tooltip title={usdTooltip}>
                                <Text variant={textVariant}>{rateDisplay}</Text>
                            </Tooltip>
                            <ExecutionRouteSteps executionDetails={executionDetails} loading={loading} />
                        </Row>
                    )
                }
            ]}
            variant={textVariant}
        />
    );
}

/** Tooltip body: one StatColumn row per hop — caption ≈ venue, value ≈ in → out */
function RouteStepTooltipContent({
    routeSteps,
    loading = false
}: {
    routeSteps: NonNullable<SwapInstructionsResponse["routeSteps"]>;
    loading?: boolean;
}) {
    const { getMetadata } = useTokenListContext();

    const stats = routeSteps.map((step) => {
        const sell = getMetadata(step.inputMint);
        const buy = getMetadata(step.outputMint);
        const exchangeType = step.type;
        const inStr = BsMetaUtil.formatAmount(sell, lamportsToUiAmount(step.inputAmount, sell?.decimals ?? 9), {
            shortSymbol: true
        });
        const outStr = BsMetaUtil.formatAmount(buy, lamportsToUiAmount(step.minOutputAmount, buy?.decimals ?? 9), {
            shortSymbol: true
        });

        return {
            caption: <ExchangeBadge exchangeName={exchangeType} loading={!!loading} />,
            value: (
                <Text variant="caption" color="body">
                    {inStr} → {outStr}
                </Text>
            )
        };
    });

    return (
        <OutlinedCard hideBorder backgroundColor="transparent">
            <Column spacing={1.5}>
                <Text variant="body2" color="body">
                    Route Steps
                </Text>
                <StatColumn loading={loading} variant="caption" spacing={1} stats={stats} />
            </Column>
        </OutlinedCard>
    );
}

function ExecutionRouteSteps({
    executionDetails,
    loading
}: {
    executionDetails: LoopExecutionDetails | undefined;
    loading: boolean;
}) {
    const routeSteps = executionDetails?.externalQuote?.routeSteps;
    const routeStepsLength = routeSteps?.length;
    if (!routeStepsLength || !routeSteps) return null;

    return (
        <Tooltip
            placement="top"
            reverseColors
            title={<RouteStepTooltipContent routeSteps={routeSteps} loading={loading} />}
            noPadding
        >
            <IconWithBackground
                tagColor="body"
                size={20}
                sx={{
                    borderRadius: "20px",
                    px: 0.75,
                    py: 0.25,
                    width: "auto",
                    maxWidth: "none",
                    minWidth: "auto",
                    height: "auto",
                    maxHeight: "none",
                    minHeight: "auto",
                    gap: 0.5
                }}
                variant="body2"
            >
                <RouteOutlined sx={{ fontSize: 20 }} />
                {routeStepsLength}
            </IconWithBackground>
        </Tooltip>
    );
}

function getExecutionErrorImpactColor({
    executionDetails,
    slippagePresetsUi
}: {
    executionDetails: LoopExecutionDetails | undefined;
    slippagePresetsUi: number[];
}): TextColor | undefined {
    if (!executionDetails || !slippagePresetsUi) return undefined;

    // Handle edge case: positive execution amounts (gains) should use default text color
    // Success color is handled explicity.
    // This function is specifically for styling execution losses/fees/slippage
    if (executionDetails.executionLossUsd >= 0) return "body";

    const absoluteLoss = Math.abs(executionDetails.executionLossUsd);
    const thresholds = slippagePresetsUi.map(
        (slippage) => percentUiToDecimals(slippage) * executionDetails.fairMarketValueUsd
    );

    if (absoluteLoss >= thresholds[2]) return "error"; // High impact: red
    if (absoluteLoss >= thresholds[0]) return "warning"; // Medium/low impact: warning

    return "body"; // Minimal impact: default color
}

function ExecutionDetailsCollapableHeader({
    executionDetails,
    loading,
    isExpanded,
    setIsExpanded,
    executionDetailsTooltip,
    textVariant,
    executionErrorImpactColor
}: {
    executionDetails: LoopExecutionDetails | undefined;
    loading: boolean;
    isExpanded: boolean;
    setIsExpanded: (isExpanded: boolean) => void;
    executionDetailsTooltip: string | undefined;
    textVariant: TextVariant;
    executionErrorImpactColor: TextColor | undefined;
}) {
    return (
        <Row spaceBetween>
            <Row spacing={1}>
                <Text color="caption" variant={textVariant} loading={loading}>
                    Execution impact
                </Text>

                {!loading && <TooltipText variant={textVariant} helpText={executionDetailsTooltip} />}
            </Row>
            <ExecutionDetailsEndValue
                loading={loading}
                executionDetails={executionDetails}
                isExpanded={isExpanded}
                setIsExpanded={setIsExpanded}
                textVariant={textVariant}
                executionErrorImpactColor={executionErrorImpactColor}
            />
        </Row>
    );
}

function ExecutionDetailsEndValue({
    loading,
    executionDetails,
    isExpanded,
    setIsExpanded,
    textVariant,
    executionErrorImpactColor
}: {
    loading: boolean;
    executionDetails: LoopExecutionDetails | undefined;
    isExpanded: boolean;
    setIsExpanded: (isExpanded: boolean) => void;
    textVariant: TextVariant;
    executionErrorImpactColor: TextColor | undefined;
}) {
    const percentColor =
        executionDetails?.executionLossUsd != null && executionDetails.executionLossUsd < 0
            ? executionErrorImpactColor
            : "success";
    return (
        <Row spacing={1}>
            {loading ? (
                <Text variant={textVariant} loading>
                    {" "}
                </Text>
            ) : (
                <PercentAndUsdRow
                    percent={executionDetails?.executionLossPercent}
                    usdValue={executionDetails?.executionLossUsd}
                    textVariant={textVariant}
                    percentColor={percentColor}
                />
            )}
            {!loading && (
                <ToggleChevron
                    onClick={() => setIsExpanded(!isExpanded)}
                    textVariant="body1"
                    sx={{ p: 0 }}
                    expanded={isExpanded}
                />
            )}
        </Row>
    );
}

function useExecutionDetailsStats({
    executionDetails,
    executionDetailsLabels,
    textVariant
}: {
    executionDetails: LoopExecutionDetails | undefined;
    executionDetailsLabels: ExecutionDetailsLabels;
    textVariant: TextVariant;
}) {
    const executionDetailsStats = useMemo(() => {
        if (!executionDetails) return [];
        const oracleSpreadPercentShown = -executionDetails.oraclePriceDeviationPercent;

        const stats: Hidable<StatProps>[] = [
            {
                caption: executionDetailsLabels.fairMarketValue?.caption ?? "Fair market value",
                tooltip: executionDetailsLabels.fairMarketValue?.tooltip,
                value: formatUsd(executionDetails.fairMarketValueUsd),
                hide: executionDetailsLabels.fairMarketValue?.hide
            },
            {
                caption: "Fees",
                value: (
                    <PercentAndUsdRow
                        percent={-executionDetails.feePercent}
                        usdValue={-executionDetails.feeUsd}
                        textVariant={textVariant}
                    />
                ),
                tooltip: executionDetailsLabels.fees?.tooltip,
                hide: !executionDetails.feeUsd || executionDetailsLabels.fees?.hide
            },
            {
                caption: "Price impact",
                value: (
                    <PercentAndUsdRow
                        percent={-executionDetails.priceImpactPercent}
                        usdValue={-executionDetails.priceImpactUsd}
                        textVariant={textVariant}
                    />
                ),
                hide: !executionDetails.priceImpactUsd || executionDetailsLabels.priceImpact?.hide
            },
            {
                caption: "Max slippage",
                value: (
                    <PercentAndUsdRow
                        percent={-executionDetails.slippagePercent}
                        usdValue={-executionDetails.slippageUsd}
                        textVariant={textVariant}
                        percentColor={executionDetails.slippagePercent < 0 ? "success" : "error"}
                    />
                ),
                hide: !executionDetails.slippageUsd || executionDetailsLabels.slippage?.hide
            },
            {
                caption: executionDetailsLabels.oracleSpread?.caption ?? "Oracle spread",
                value: (
                    <PercentAndUsdRow
                        percent={oracleSpreadPercentShown}
                        usdValue={-executionDetails.oraclePriceDeviationUsd}
                        textVariant={textVariant}
                        percentColor={oracleSpreadPercentShown > 0 ? "success" : "error"}
                    />
                ),
                tooltip: executionDetailsLabels.oracleSpread?.tooltip,
                hide: !Math.abs(executionDetails.oraclePriceDeviationUsd) || executionDetailsLabels.oracleSpread?.hide
            },
            {
                caption: executionDetailsLabels.receiveOnClose?.caption ?? "Receive on close",
                tooltip: executionDetailsLabels.receiveOnClose?.tooltip,
                value: formatUsd(executionDetails.receiveUsdAmount),
                hide: executionDetailsLabels.receiveOnClose?.hide
            }
        ].filter((s) => !s.hide);

        return stats;
    }, [executionDetails, executionDetailsLabels, textVariant]);

    return executionDetailsStats;
}
